import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { loginApi } from '../api';
import {
  getStoredAuthToken,
  getStoredAuthUserJson,
  persistAuthCredentials,
  clearAuthCredentials,
} from '../utils/secureStorage';
import {
  clearAllAppCache,
  broadcastLogout,
  initTabSessionLifecycle,
} from '../utils/cacheManager';

const AuthContext = createContext();
const INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000;
const LAST_ACTIVITY_KEY = 'authLastActivityAt';

const str = (v) => (v === undefined || v === null ? '' : String(v).toUpperCase());
const isTrueish = (v) =>
  v === true || v === 1 || str(v) === 'TRUE' || str(v) === 'VERIFIED' || str(v) === 'APPROVED';

function getStoredLastActivityAt() {
  if (typeof sessionStorage !== 'undefined') {
    const raw = sessionStorage.getItem(LAST_ACTIVITY_KEY);
    const value = Number(raw);
    if (Number.isFinite(value) && value > 0) return value;
  }
  if (typeof localStorage !== 'undefined') {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const value = Number(raw);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return null;
}

function setStoredLastActivityAt(value = Date.now()) {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(LAST_ACTIVITY_KEY, String(value));
  }
  // Remove from localStorage so closing the tab ends the session
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  }
}

function clearStoredLastActivityAt() {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(LAST_ACTIVITY_KEY);
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  }
}

function hasVerificationFields(inner) {
  return (
    inner &&
    typeof inner === 'object' &&
    (Object.hasOwn(inner, 'isEmailVerified') ||
      Object.hasOwn(inner, 'isDocumentUploaded') ||
      Object.hasOwn(inner, 'statusCode'))
  );
}

function setAccountVerificationStep(step) {
  try {
    sessionStorage.setItem('av_verificationStep', step);
  } catch {
    // ignore
  }
}

const buildVerifyUrl = (step, email, phone) => {
  const params = new URLSearchParams({ step, email });
  if (phone) params.set('phone', phone);
  return `/verify?${params.toString()}`;
};

function deriveLoginVerificationFlags(status) {
  const emailVerified = isTrueish(status?.emailVerified ?? status?.isEmailVerified);
  const phoneVerified = isTrueish(status?.phoneVerified ?? status?.isPhoneVerified);
  const documentsVerified = isTrueish(
    status?.documentVerified ?? status?.documentsVerified ?? status?.isDocumentVerified
  );
  const fullyVerified = isTrueish(
    status?.isFullyVerified ?? status?.fullyVerified ?? status?.verificationComplete
  );
  const canAccessDashboard = isTrueish(status?.canAccessDashboard);
  const hasDocUploadedKey = Object.hasOwn(status, 'isDocumentUploaded');
  const docUploadStage =
    status?.statusCode === 4 ||
    str(status?.status) === 'DOCUMENT_UPLOAD' ||
    str(status?.verificationStatus) === 'DOCUMENT_UPLOAD';
  const needsDocumentUpload = hasDocUploadedKey
    ? !isTrueish(status.isDocumentUploaded)
    : docUploadStage || !documentsVerified;
  const isSubmissionConfirmed = isTrueish(status?.isSubmissionConfirmed);

  return {
    emailVerified,
    phoneVerified,
    fullyVerified,
    canAccessDashboard,
    needsDocumentUpload,
    isSubmissionConfirmed,
    documentsVerified,
    hasDocUploadedKey,
    documentUploaded: hasDocUploadedKey ? isTrueish(status.isDocumentUploaded) : false,
  };
}

export function getLoginSuccessToastMessage(status) {
  const hasStatus = Object.keys(status || {}).length > 0;
  if (!hasStatus) {
    return { severity: 'success', message: 'Welcome to Welivin!' };
  }

  const {
    emailVerified,
    phoneVerified,
    fullyVerified,
    canAccessDashboard,
    needsDocumentUpload,
    documentsVerified,
    hasDocUploadedKey,
    documentUploaded,
  } = deriveLoginVerificationFlags(status);

  if (!emailVerified) {
    return { severity: 'info', message: 'Please verify your email.' };
  }
  if (!phoneVerified) {
    return { severity: 'info', message: 'Please verify your phone number.' };
  }
  if (needsDocumentUpload) {
    return { severity: 'info', message: 'Please upload your verification documents.' };
  }
  if (hasDocUploadedKey && documentUploaded && !documentsVerified) {
    return { severity: 'info', message: 'Your documents are being reviewed. Verification is in progress.' };
  }
  if (!fullyVerified && !canAccessDashboard) {
    return { severity: 'info', message: 'Please complete your verification.' };
  }
  return { severity: 'success', message: 'Welcome to Welivin!' };
}

function resolvePostLoginPath(flags, { email, phone, userVerified, hasStatus }) {
  if (hasStatus) {
    const {
      emailVerified,
      phoneVerified,
      fullyVerified,
      canAccessDashboard,
      needsDocumentUpload,
      isSubmissionConfirmed,
    } = flags;

    if (!emailVerified) return buildVerifyUrl('email', email, phone);
    if (!phoneVerified) return buildVerifyUrl('phone', email, phone);
    if (fullyVerified || canAccessDashboard) return '/dashboard';
    if (needsDocumentUpload) {
      setAccountVerificationStep('form');
      return '/account-verification';
    }
    if (!isSubmissionConfirmed) {
      setAccountVerificationStep('preview');
      return '/account-verification';
    }
    return buildVerifyUrl('pending', email);
  }

  return userVerified ? '/dashboard' : '/account-verification';
}

/**
 * Login may return verification either as `verificationStatus` or flattened on `data.data`
 * (e.g. isEmailVerified, isDocumentUploaded, statusCode).
 */
export function pickLoginVerificationStatus(loginData) {
  if (!loginData || typeof loginData !== 'object') return {};
  if (loginData.verificationStatus && typeof loginData.verificationStatus === 'object') {
    return loginData.verificationStatus;
  }
  const inner = loginData.data;
  if (inner?.verificationStatus && typeof inner.verificationStatus === 'object') {
    return inner.verificationStatus;
  }
  if (hasVerificationFields(inner)) {
    return inner;
  }
  const u = loginData.user;
  if (u?.verificationStatus && typeof u.verificationStatus === 'object') {
    return u.verificationStatus;
  }
  return {};
}

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => getStoredAuthToken());
  const [user, setUser] = useState(() => getStoredAuthUserJson());

  const logout = useCallback(() => {
    clearStoredLastActivityAt();
    clearAuthCredentials();
    clearAllAppCache({ preserveLanguage: true });
    broadcastLogout();
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const cleanup = initTabSessionLifecycle(() => {
      clearStoredLastActivityAt();
      clearAuthCredentials();
      clearAllAppCache({ preserveLanguage: true });
      setToken(null);
      setUser(null);
      navigate('/login', { replace: true });
    });
    return cleanup;
  }, [navigate]);

  useEffect(() => {
    persistAuthCredentials({
      token: token && String(token).length ? token : null,
      user: user ?? null,
    });
  }, [token, user]);

  useEffect(() => {
    if (!token) return undefined;

    let timeoutId;
    let intervalId;

    const expireInactiveSession = () => {
      logout();
      navigate('/login', { replace: true });
    };

    const scheduleExpiry = () => {
      window.clearTimeout(timeoutId);
      const lastActivityAt = getStoredLastActivityAt() || Date.now();
      const expiresIn = INACTIVITY_TIMEOUT_MS - (Date.now() - lastActivityAt);

      if (expiresIn <= 0) {
        expireInactiveSession();
        return;
      }

      timeoutId = window.setTimeout(expireInactiveSession, expiresIn);
    };

    const markClickActivity = () => {
      setStoredLastActivityAt();
      scheduleExpiry();
    };

    if (getStoredLastActivityAt() === null) {
      setStoredLastActivityAt();
    }

    scheduleExpiry();
    window.addEventListener('pointerdown', markClickActivity, true);
    window.addEventListener('click', markClickActivity, true);
    intervalId = window.setInterval(scheduleExpiry, 60 * 1000);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
      window.removeEventListener('pointerdown', markClickActivity, true);
      window.removeEventListener('click', markClickActivity, true);
    };
  }, [token, navigate, logout]);

  const login = useCallback(async (email, password) => {
    const { data } = await loginApi(email, password);
    if (data?.success) {
      // Support both response shapes:
      // 1) { success, token, user, verificationStatus }
      // 2) { success, data: { token?, ...userFields, verificationStatus? } }
      const token = data?.token ?? data?.data?.token ?? null;
      const userData =
        data?.user ??
        (data?.data && typeof data.data === 'object' ? data.data : null);

      setStoredLastActivityAt();
      if (token) setToken(token);
      setUser(userData);

      const status = pickLoginVerificationStatus(data);
      const hasStatus = Object.keys(status || {}).length > 0;
      const phone = data?.phoneNumber || userData?.phoneNumber || userData?.phone || '';
      const flags = deriveLoginVerificationFlags(status);
      const path = resolvePostLoginPath(flags, {
        email,
        phone,
        userVerified: Boolean(userData?.isVerified),
        hasStatus,
      });

      navigate(path);
    }
    return data;
  }, [navigate]);

  const value = useMemo(
    () => ({ token, user, login, logout, isAuthenticated: !!token, setToken, setUser }),
    [token, user, login, logout, setToken, setUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
